import { StyledText } from 'UI';
import { useClientSize } from 'hooks';
import { FC } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import { selectors, useAppSelector } from 'store';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

import BundleCard from './components/BundleCard';

type YourBundlesProps = {
  isLoading: boolean;
};

const YourBundles: FC<YourBundlesProps> = ({ isLoading }) => {
  const { getIsBreakpoint } = useClientSize();
  const { colors } = useTheme();
  const isWidthSm = getIsBreakpoint('sm');

  const courses = useAppSelector(selectors.courses.selectCourses);

  // Filter bundles from the courses list
  const bundles = courses?.filter((course) => (course.type as string) === 'bundle') || [];

  if (isLoading) {
    return (
      <Root>
        <Header>
          <StyledText font={isWidthSm ? 'body_large_semibold_16' : 'headline_semibold_18'}>Your Bundles</StyledText>
        </Header>
        <LoaderWrapper>
          <ThreeDots
            height="30"
            width="30"
            radius="9"
            color={colors.primary[1]}
            ariaLabel="three-dots-loading"
            visible
          />
        </LoaderWrapper>
      </Root>
    );
  }

  if (bundles.length === 0) {
    return (
      <Root>
        <Header>
          <StyledText font={isWidthSm ? 'body_large_semibold_16' : 'headline_semibold_18'}>Your Bundles</StyledText>
        </Header>
        <EmptyState>
          <StyledText
            font="body_basic_regular_14"
            customStyles={css`
              color: ${colors.neutrals[5]};
            `}>
            You don&apos;t have any bundles yet. Purchase a bundle to see it here.
          </StyledText>
        </EmptyState>
      </Root>
    );
  }

  return (
    <Root>
      <Header>
        <StyledText font={isWidthSm ? 'body_large_semibold_16' : 'headline_semibold_18'}>
          Your Bundles ({bundles.length})
        </StyledText>
      </Header>
      <BundlesContainer>
        {bundles.map((bundle) => (
          <BundleCard key={bundle.id} bundle={bundle} />
        ))}
      </BundlesContainer>
    </Root>
  );
};

const Root = styled.div`
  ${({ theme: { colors } }) => css`
    display: flex;
    flex-direction: column;
    background-color: ${colors.neutrals[11]};
    border-radius: 8px;
    border: 1px solid ${colors.neutrals[9]};
    padding: 24px;

    ${respondToWidth.sm`
      padding: 16px;
      border: none;
      background-color: transparent;
    `}
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  text-align: center;
`;

const BundlesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  ${respondToWidth.sm`
    grid-template-columns: 1fr;
  `}
`;

export default YourBundles;
